import { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContextBackend';

interface UserProfile {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    profile_pic_url: string | null;
    phone?: string | null;
    job_title?: string | null;
    department?: { name: string } | null;
    team?: { name: string } | null;
    location?: { name: string } | null;
}

interface Person {
    id: number;
    user_uuid?: string;
    email: string;
    first_name: string;
    last_name: string;
    job_title?: string | null;
    profile_pic_url: string | null;
}

interface MeResponse {
    user: UserProfile;
    account: {
        id: number;
        name: string;
        company_name?: string;
        external_id?: string;
    };
    managers: Person[];
    reportees: Person[];
}

interface UseCoreBackendDataProps {
    templateKey?: string;
    metricKey?: string;
    queryConfig?: any;
    chartType?: string;
}

export const useCoreBackendData = ({
    templateKey,
    metricKey,
    queryConfig,
    chartType
}: UseCoreBackendDataProps) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memoize the data source to avoid unnecessary re-fetches
    const dataSource = queryConfig?.data_source;

    // Determine if this is a core widget using primitive values
    const isCoreWidget = useMemo(() => {
        return templateKey === 'mp_user_card' ||
            chartType === 'profile_card' ||
            metricKey?.startsWith('core_api:') ||
            dataSource === 'core_backend';
    }, [templateKey, chartType, metricKey, dataSource]);

    useEffect(() => {
        // Wait for auth to be ready before making requests
        if (!isCoreWidget || authLoading || !isAuthenticated) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch user profile with managers and reportees from single /me endpoint
                const userResponse = await apiClient.get<MeResponse>('/me');
                // Handle both axios-style (response.data) and direct response
                const responseData = userResponse.data || userResponse;
                const userData = (responseData as MeResponse).user;
                const account = (responseData as MeResponse).account;
                const managersData = (responseData as MeResponse).managers || [];
                const reporteesData = (responseData as MeResponse).reportees || [];

                if (!userData) {
                    console.error('Invalid /me response structure:', responseData);
                    setError('Invalid user data response');
                    setLoading(false);
                    return;
                }

                // Transform managers array to expected format
                const transformedManagers = managersData.map(m => ({
                    full_name: `${m.first_name} ${m.last_name || ''}`.trim(),
                    email: m.email,
                    job_title: m.job_title || null,
                    profile_pic_url: m.profile_pic_url || null
                }));

                // Transform reportees array to expected format
                const transformedReportees = reporteesData.map(r => ({
                    full_name: `${r.first_name} ${r.last_name || ''}`.trim(),
                    email: r.email,
                    job_title: r.job_title || null,
                    profile_pic_url: r.profile_pic_url || null
                }));

                setData({
                    profile_pic_url: userData.profile_pic_url || null,
                    full_name: `${userData.first_name} ${userData.last_name || ''}`.trim(),
                    email: userData.email,
                    phone_number: userData.phone || null,
                    job_title: userData.job_title || null,
                    department: userData.department?.name || null,
                    team: userData.team?.name || null,
                    location: userData.location?.name || null,
                    company_name: account?.company_name || account?.name || null,
                    // Primary manager (first in array)
                    manager: transformedManagers[0] || null,
                    // All managers (for displaying multiple managers if needed)
                    managers: transformedManagers,
                    // All reportees
                    reportees: transformedReportees
                });
            } catch (err) {
                console.error('Failed to fetch core backend data:', err);
                setError('Failed to load user data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isCoreWidget, authLoading, isAuthenticated]);

    return { data, loading, error };
};
